import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Trash2, ShieldCheck } from "lucide-react";

export default function Admins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/admins");
    setAdmins(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const removeAdmin = async () => {
    try {
      await api.delete(`/admin/admins/${removeTarget.id}`);
      toast.success("Administrator removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setRemoveTarget(null);
      load();
    }
  };

  return (
    <DashboardLayout nav={adminNav} title="Administrators">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Administrators</h1>
        <p className="text-muted-foreground mt-1">Manage platform administrators. You can't remove your own account.</p>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Institution</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
            )}
            {!loading && admins.map((a) => {
              const isSelf = a.id === user?.id;
              return (
                <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-primary" /> {a.name}
                      {isSelf && <span className="text-[10px] uppercase tracking-wider bg-accent text-accent-foreground px-1.5 py-0.5">You</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{a.email}</TableCell>
                  <TableCell className="text-sm">{a.institution}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive disabled:opacity-30"
                      disabled={isSelf}
                      title={isSelf ? "You can't remove yourself" : "Remove admin"}
                      onClick={() => setRemoveTarget(a)}
                      data-testid={`remove-admin-${a.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently revokes administrator access for {removeTarget?.email}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeAdmin} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-remove-admin">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
